import type {Object3D, Texture} from "three";
import {
	BufferAttribute,
	BufferGeometry,
	CircleGeometry,
	DynamicDrawUsage,
	LineBasicMaterial,
	LineDashedMaterial,
	LineSegments,
	Mesh,
	MeshBasicMaterial,
	Object3D as ThreeObject3D,
} from "three";
import {CatalogIconType, MarkupType, type PointDouble} from "../../../../../../../generated/api/base";
import type {Xyicon} from "../../../../../../../data/models/Xyicon";
import type {IBox2} from "../../../../../../../utils/THREEUtils";
import {THREEUtils} from "../../../../../../../utils/THREEUtils";
import {MathUtils} from "../../../../../../../utils/math/MathUtils";
import {DebugInformation} from "../../../../../../../utils/DebugInformation";
import {Constants} from "../../Constants";
import type {ItemSelectionBox} from "../../elements3d/ItemSelectionBox";
import type {SpaceItem} from "../../elements3d/SpaceItem";
import type {Markup3D} from "../../elements3d/markups/abstract/Markup3D";
import type {SpaceViewRenderer} from "../../renderers/SpaceViewRenderer";
import {getTextSelectionRect} from "../MSDF/TextUtils";
import type {
	SelectionHandleCorner,
	ISelectionHandleDescriptor,
	SelectionHandleKind,
	SelectionHandleOwnerType,
	SelectionHandlePhase,
} from "./SelectionHandleUtils";
import {
	getBoxCornerPoint,
	getBoxRotationHandleCornerRadius,
	getBoxRotationHandleInsetPoint,
	getBoxRotationHandleTriggerOutline,
	getBoxRotationHandleZoneCenter,
	getBoxRotationHandleZoneOutline,
	getVisibleRotationHandleState,
	getLinearSelectionHandleRotation,
	getBoxRotationHandleIconCenter,
	getBoxRotationHandleIconRotation,
	getLinearRotationHandleIconCenter,
	getMarkupSelectionPadding,
	getRotationHandleOffsetDistance,
	getRotationHandleIconRotation,
	getRotationHandleSectorOuterRadius,
	getRotationHandleSectorStartAngle,
	getSelectionBoxBoundingBox,
	getSelectionBoxTopCenter,
	getSelectionHandlePriority,
	getSelectionHandleSameAnchorThreshold,
	getWorldSizeFromPixels,
	GROUP_ROTATION_HANDLE_SIZE_PX,
	HANDLE_HIT_SIZE_PX,
	createZone,
	doSelectionHandleAnchorsMatch,
	doesV5SelectionHandleAllowRotationForPermission,
	doesV5LinearMarkupSupportIndependentTextBoxControls,
	isV5LinearMarkupType,
	mergeBoundingBoxes,
	ROTATION_HANDLE_SECTOR_SEGMENTS,
	ROTATION_HANDLE_SIZE_PX,
	selectionHandleCorners,
	SCALE_HANDLE_SIZE_PX,
	STANDARD_XYICON_CORNER_RADIUS_RATIO,
	suppressSelectionHandlesWithSameAnchor,
} from "./SelectionHandleUtils";

const selectionColor = Constants.COLORS.SELECTION_BOX_V5;
const rotationHandleZoneDebugColor = 0xf0cc29;
const rotationHandleTriggerDebugColor = 0xff0000;
const interactiveHandleName = "selectionHandle";

export type SelectionHandleActionType = "rotate" | "rotateText" | "scale" | "scaleLineEndpoint" | "scaleText" | "anchor" | "moveTextAnchor";

export interface ISelectionHandleAction {
	type: SelectionHandleActionType;
	id: string;
	rotateTarget?: SpaceItem | ItemSelectionBox;
	item?: Markup3D;
	corner?: SelectionHandleCorner;
	endpointIndex?: 0 | 1;
}

export interface ISelectionHandleUserData {
	id: string;
	phase: SelectionHandlePhase;
	action: ISelectionHandleAction;
	anchorCenter?: PointDouble;
	zoneStartsAction?: boolean;
	hoverHandleId?: string;
	kind?: SelectionHandleKind;
	ownerType?: SelectionHandleOwnerType;
}

interface ISelectionRect {
	center: PointDouble;
	size: PointDouble;
	orientation: number;
}

interface IRotationHandleMeshes {
	zone: Mesh;
	icon: Mesh;
	triggers: Mesh[];
}

interface ILineSegmentDefinition {
	start: PointDouble;
	end: PointDouble;
}

interface IRotationHandleDefinition {
	descriptor: ISelectionHandleDescriptor;
	action: ISelectionHandleAction;
	triggerCenter?: PointDouble;
	iconCenter?: PointDouble;
	iconRotation?: number;
	iconSizePx: number;
	zoneRotation?: number;
	zoneStartsAction?: boolean;
	/**
	 * Only for box corners (where `triggerCenter` is the corner itself): the radii of the item's rounded corner in the corner's local frame
	 * (see `getBoxRotationHandleCornerRadius`), so the zone and the trigger can hug it. Zero for sharp corners.
	 */
	cornerRadius?: PointDouble;
}

const disableRaycast = (object: Object3D): void => {
	(object as any).raycast = (): void => undefined;
};

const setRaycastEnabled = (object: Object3D, enabled: boolean): void => {
	if (enabled) {
		delete (object as any).raycast;
		return;
	}

	disableRaycast(object);
};

/** For star-shaped outlines around the local origin: vertex 0 is the origin, the rest is the outline */
const createFanGeometry = (outlineVertexCount: number) => {
	const geometry = new BufferGeometry();
	const positionAttribute = new BufferAttribute(new Float32Array((outlineVertexCount + 1) * 3), 3);
	const indices: number[] = [];

	for (let i = 1; i <= outlineVertexCount; ++i) {
		indices.push(0, i, (i % outlineVertexCount) + 1);
	}

	positionAttribute.setUsage(DynamicDrawUsage);
	geometry.setAttribute("position", positionAttribute);
	geometry.setIndex(indices);

	return geometry;
};

const updateFanGeometry = (geometry: BufferGeometry, outline: PointDouble[]) => {
	const positionAttribute = geometry.getAttribute("position") as BufferAttribute;

	for (let i = 0; i < outline.length; ++i) {
		positionAttribute.setXY(i + 1, outline[i].x, outline[i].y);
	}

	positionAttribute.needsUpdate = true;
	// Needed for raycasting too, not just for frustum culling
	geometry.computeBoundingSphere();
};

class SelectionLineBatch {
	private readonly _geometry = new BufferGeometry();
	private readonly _line: LineSegments;
	private _positionAttribute: BufferAttribute | null = null;
	private _lineDistanceAttribute: BufferAttribute | null = null;
	private _capacityVertexCount = 0;

	constructor(
		container: Object3D,
		private readonly _material: LineBasicMaterial | LineDashedMaterial,
		private readonly _isDashed: boolean = false,
	) {
		this._line = new LineSegments(this._geometry, this._material);
		this._line.frustumCulled = false;
		this._line.matrixAutoUpdate = false;
		this._line.visible = false;
		disableRaycast(this._line);
		container.add(this._line);
	}

	private ensureCapacity(vertexCount: number) {
		if (vertexCount <= this._capacityVertexCount) {
			return;
		}

		this._capacityVertexCount = Math.max(vertexCount, Math.max(this._capacityVertexCount * 2, 8));

		const positionAttribute = new BufferAttribute(new Float32Array(this._capacityVertexCount * 3), 3);

		positionAttribute.setUsage(DynamicDrawUsage);
		this._geometry.setAttribute("position", positionAttribute);
		this._positionAttribute = positionAttribute;

		if (this._isDashed) {
			const lineDistanceAttribute = new BufferAttribute(new Float32Array(this._capacityVertexCount), 1);

			lineDistanceAttribute.setUsage(DynamicDrawUsage);
			this._geometry.setAttribute("lineDistance", lineDistanceAttribute);
			this._lineDistanceAttribute = lineDistanceAttribute;
		}
	}

	public update(segments: ILineSegmentDefinition[], zPosition: number) {
		const vertexCount = segments.length * 2;

		this.ensureCapacity(vertexCount);
		this._geometry.setDrawRange(0, vertexCount);
		this._line.visible = vertexCount > 0;
		this._line.position.set(0, 0, zPosition);

		if (vertexCount === 0 || !this._positionAttribute) {
			this._line.updateMatrix();
			return;
		}

		const positions = this._positionAttribute.array as Float32Array;
		const lineDistances = this._lineDistanceAttribute?.array as Float32Array | undefined;
		let positionIndex = 0;
		let lineDistanceIndex = 0;

		for (const segment of segments) {
			positions[positionIndex++] = segment.start.x;
			positions[positionIndex++] = segment.start.y;
			positions[positionIndex++] = 0;
			positions[positionIndex++] = segment.end.x;
			positions[positionIndex++] = segment.end.y;
			positions[positionIndex++] = 0;

			if (lineDistances) {
				const length = Math.hypot(segment.end.x - segment.start.x, segment.end.y - segment.start.y);

				lineDistances[lineDistanceIndex++] = 0;
				lineDistances[lineDistanceIndex++] = length;
			}
		}

		this._positionAttribute.needsUpdate = true;
		if (this._lineDistanceAttribute) {
			this._lineDistanceAttribute.needsUpdate = true;
		}

		this._line.updateMatrix();
	}

	public dispose() {
		this._line.parent?.remove(this._line);
		this._geometry.dispose();
	}
}

export class SelectionHandleManager {
	private readonly _spaceViewRenderer: SpaceViewRenderer;
	private readonly _container = new ThreeObject3D();
	private readonly _visibleObjects: (Object3D | {dispose: () => void})[] = [];
	private readonly _selectionBoxSegments: ILineSegmentDefinition[] = [];
	private readonly _lineOverlaySegments: ILineSegmentDefinition[] = [];
	private readonly _textConnectorSegments: ILineSegmentDefinition[] = [];
	private readonly _currentRotationHandleIds: Set<string> = new Set();
	private readonly _rotationHandleMeshes: Map<string, IRotationHandleMeshes> = new Map();
	private readonly _selectionLineMaterial = new LineBasicMaterial({color: selectionColor, depthTest: false});
	private readonly _dashedSelectionLineMaterial = new LineDashedMaterial({color: selectionColor, depthTest: false});
	private readonly _rotationHandleZoneMaterial = new MeshBasicMaterial({color: rotationHandleZoneDebugColor, depthTest: false, depthWrite: false});
	private readonly _rotationHandleTriggerMaterial = new MeshBasicMaterial({
		color: rotationHandleTriggerDebugColor,
		depthTest: false,
		depthWrite: false,
	});
	private readonly _rotationHandleSectorGeometry = new CircleGeometry(1, ROTATION_HANDLE_SECTOR_SEGMENTS, 0, Math.PI / 2);
	private readonly _selectionBoxBatch: SelectionLineBatch;
	private readonly _lineOverlayBatch: SelectionLineBatch;
	private readonly _textConnectorBatch: SelectionLineBatch;
	private _rotationIconMaterial: MeshBasicMaterial | null = null;
	private _groupRotationIconMaterial: MeshBasicMaterial | null = null;
	private _hoveredRotationHandleId: string | null = null;
	private _activeRotationHandleId: string | null = null;

	constructor(spaceViewRenderer: SpaceViewRenderer) {
		this._spaceViewRenderer = spaceViewRenderer;
		this._container.name = "selectionHandleContainer";
		this._selectionBoxBatch = new SelectionLineBatch(this._container, this._selectionLineMaterial);
		this._lineOverlayBatch = new SelectionLineBatch(this._container, this._selectionLineMaterial);
		this._textConnectorBatch = new SelectionLineBatch(this._container, this._dashedSelectionLineMaterial, true);
	}

	public init() {
		THREEUtils.add(this._spaceViewRenderer.topLayerScene, this._container);
	}

	public clear() {
		this.clearRenderedObjects();
		this._hoveredRotationHandleId = null;
		this._activeRotationHandleId = null;
	}

	public update() {
		this.beginRenderPass();

		if (!this._spaceViewRenderer.isV5 || this._spaceViewRenderer.toolManager.isInLinkMode || this._spaceItemController.isInEditMode) {
			this.resetRenderedState();
			this._hoveredRotationHandleId = null;
			this._activeRotationHandleId = null;
			this._spaceViewRenderer.needsRender = true;
			return;
		}

		const selectedItems = this.getSelectedCanvasItems();

		if (selectedItems.length === 0) {
			this.resetRenderedState();
			this._hoveredRotationHandleId = null;
			this._activeRotationHandleId = null;
			this._spaceViewRenderer.needsRender = true;
			return;
		}

		if (selectedItems.length === 1) {
			this.renderSingleSelection(selectedItems[0]);
		} else {
			this.renderGroupSelection(selectedItems);
		}

		this.finishRenderPass();
		this.applyVisibleRotationHandleState();

		this._spaceViewRenderer.needsRender = true;
	}

	public updateTransformations() {
		this.update();
	}

	public clearHover() {
		if (this._hoveredRotationHandleId === null) {
			return;
		}

		this._hoveredRotationHandleId = null;
		this.applyVisibleRotationHandleState();
	}

	public setHoveredHandle(handleId: string | null) {
		if (this._hoveredRotationHandleId === handleId) {
			if (handleId && !this._rotationHandleMeshes.get(handleId)?.icon.visible) {
				this.applyVisibleRotationHandleState();
			}

			return;
		}

		this._hoveredRotationHandleId = handleId;
		this.applyVisibleRotationHandleState();
	}

	public setActiveRotationHandle(handleId: string | null) {
		if (this._activeRotationHandleId === handleId) {
			return;
		}

		this._activeRotationHandleId = handleId;
		this.applyVisibleRotationHandleState();
	}

	public clearActiveRotationHandle() {
		this.setActiveRotationHandle(null);
	}

	private applyVisibleRotationHandleState() {
		const {activeRotationHandleId, hoveredRotationHandleId, visibleRotationHandleId} = getVisibleRotationHandleState(
			this._activeRotationHandleId,
			this._hoveredRotationHandleId,
			new Set(this._rotationHandleMeshes.keys()),
		);

		this._activeRotationHandleId = activeRotationHandleId;
		this._hoveredRotationHandleId = hoveredRotationHandleId;

		for (const [id, meshes] of this._rotationHandleMeshes) {
			const isVisible = id === visibleRotationHandleId;

			meshes.icon.visible = isVisible;
			for (const trigger of meshes.triggers) {
				trigger.visible = this.isRotationHandleDebugVisible || isVisible;
				setRaycastEnabled(trigger, isVisible);
			}
		}

		this._spaceViewRenderer.needsRender = true;
	}

	public getHandleData(object: Object3D) {
		return object?.userData?.selectionHandle as ISelectionHandleUserData | undefined;
	}

	public get container() {
		return this._container;
	}

	public getSelectedCanvasBoundingBox(items: SpaceItem[] = this.getSelectedCanvasItems()) {
		return mergeBoundingBoxes(items.map((item) => this.getSelectionBoundingBox(item)).filter((bbox): bbox is IBox2 => bbox != null));
	}

	private renderSingleSelection(item: SpaceItem) {
		const selectionRect = this.getSelectionRectangle(item);
		const allowRotation = this.canShowRotationHandleForItem(item);

		if (selectionRect) {
			this.createSelectionBox(selectionRect);
		}

		if (item.isPositionLockedAnyWay) {
			if (item.spaceItemType === "markup" && isV5LinearMarkupType((item as Markup3D).type)) {
				this.createLineOverlay(item as Markup3D);
				this.createLinearMarkupTextBox(item as Markup3D, false, false, true);
			}
			return;
		}

		if (item.spaceItemType === "markup") {
			const markup = item as Markup3D;

			if (isV5LinearMarkupType(markup.type)) {
				this.createLineOverlay(markup);
				this.createLinearMarkupHandles(markup, allowRotation);
				this.createLinearMarkupTextBox(markup, true, allowRotation);
				return;
			}

			if (selectionRect) {
				this.createBoxHandles(selectionRect, {
					rotateTarget: markup,
					item: markup,
					allowScaling: ![MarkupType.Photo360].includes(markup.type),
					allowRotation,
				});
			}

			if (markup.type === MarkupType.Callout) {
				this.createCalloutAnchorHandle(markup, true);
			}

			return;
		}

		if (selectionRect) {
			this.createBoxHandles(selectionRect, {
				rotateTarget: item,
				allowScaling: false,
				allowRotation,
			});
		}
	}

	private renderGroupSelection(items: SpaceItem[]) {
		const descriptors: IRotationHandleDefinition[] = [];
		const hasLockedItems = items.some((item) => item.isPositionLockedAnyWay);
		const itemsWithRotationPermission = items.filter((item) => this.canShowRotationHandleForItem(item));

		if (!hasLockedItems && itemsWithRotationPermission.length > 0) {
			const groupSelectionBox = this._spaceItemController.boundingBox;
			const groupRect = this.getSelectionRectangleFromBoundingBox(groupSelectionBox.boundingBox, groupSelectionBox.orientation);

			descriptors.push(...this.buildBoxRotationHandleDescriptors(groupRect, groupSelectionBox, "group", GROUP_ROTATION_HANDLE_SIZE_PX, null, true));
			for (const item of itemsWithRotationPermission) {
				const ownerType = this.getOwnerType(item);

				if (item.spaceItemType === "markup" && isV5LinearMarkupType((item as Markup3D).type)) {
					descriptors.push(...this.buildLinearRotationHandleDescriptors(item as Markup3D, ownerType, item));
					continue;
				}

				const rect = this.getSelectionRectangle(item);

				if (rect) {
					descriptors.push(...this.buildBoxRotationHandleDescriptors(rect, item, ownerType, ROTATION_HANDLE_SIZE_PX, null, true));
				}
			}
		}

		for (const handle of suppressSelectionHandlesWithSameAnchor(
			descriptors.map((entry) => entry.descriptor),
			this.getWorldSameAnchorThreshold(),
			this.getPinnedRotationHandleIds(),
		)) {
			const entry = descriptors.find((candidate) => candidate.descriptor.id === handle.id);

			if (entry) {
				this.renderRotationHandle(
					entry.descriptor.ownerType === "group" ? {...entry, cornerRadius: this.getGroupCornerRadius(entry, descriptors)} : entry,
				);
			}
		}

		for (const item of items) {
			this.renderGroupItemSelection(item);
		}
	}

	private buildBoxRotationHandleDescriptors(
		rect: ISelectionRect,
		rotateTarget: SpaceItem | ItemSelectionBox,
		ownerType: SelectionHandleOwnerType,
		iconSizePx: number,
		iconOffsetDistance: number = null,
		zoneStartsAction: boolean = false,
	): IRotationHandleDefinition[] {
		const hitSize = this.getWorldHitSize();
		const priority = getSelectionHandlePriority(ownerType);
		const descriptors: IRotationHandleDefinition[] = [];
		const bbox = this.getBoundingBoxFromRect(rect);
		const cornerRadiusRatio = this.getCornerRadiusRatio(rotateTarget);

		for (const corner of selectionHandleCorners) {
			const point = getBoxCornerPoint(bbox, corner, rect.orientation);
			const insetPoint = getBoxRotationHandleInsetPoint(
				point,
				corner,
				this._spaceViewRenderer.correctionMultiplier.current,
				this.cameraZoomLevel,
				rect.orientation,
			);
			const iconRotation = getBoxRotationHandleIconRotation(corner, rect.orientation);
			const iconCenter = this.getCornerRotationIconCenter(insetPoint, corner, rect.orientation, iconSizePx, iconOffsetDistance);
			const descriptor = {
				id: `${ownerType}-rotate-${corner}-${(rotateTarget as SpaceItem).id ?? "group"}`,
				kind: ownerType === "group" ? "groupRotate" : "rotate",
				ownerType,
				priority,
				zone: createZone(getBoxRotationHandleZoneCenter(point, corner, hitSize, rect.orientation), hitSize),
				corner,
				hoverVisible: true,
			} as ISelectionHandleDescriptor;

			descriptors.push({
				descriptor,
				action: {
					type: "rotate",
					id: descriptor.id,
					rotateTarget,
				},
				zoneRotation: rect.orientation,
				zoneStartsAction,
				triggerCenter: point,
				iconCenter,
				iconRotation,
				iconSizePx,
				cornerRadius: getBoxRotationHandleCornerRadius(corner, rect.size, cornerRadiusRatio),
			});
		}

		return descriptors;
	}

	private buildLinearRotationHandleDescriptors(
		markup: Markup3D,
		ownerType: SelectionHandleOwnerType,
		rotateTarget: SpaceItem,
	): IRotationHandleDefinition[] {
		const points = this.getDisplayedMarkupPoints(markup);

		if (points.length < 2) {
			return [];
		}

		const hitSize = this.getWorldHitSize();
		const priority = getSelectionHandlePriority(ownerType);
		const iconOffset = getRotationHandleOffsetDistance(
			ROTATION_HANDLE_SIZE_PX,
			this._spaceViewRenderer.correctionMultiplier.current,
			this.cameraZoomLevel,
		);

		return points.map((point: PointDouble, endpointIndex: number) => {
			const iconCenter = getLinearRotationHandleIconCenter(points[0], points[1], endpointIndex as 0 | 1, iconOffset);
			const descriptor = {
				id: `${ownerType}-rotate-line-${endpointIndex}-${markup.id}`,
				kind: "rotate",
				ownerType,
				priority,
				zone: createZone(point, hitSize),
				hoverVisible: true,
			} as ISelectionHandleDescriptor;

			return {
				descriptor,
				action: {
					type: "rotate",
					id: descriptor.id,
					rotateTarget,
				},
				zoneRotation: getLinearSelectionHandleRotation(points[0], points[1]),
				triggerCenter: point,
				iconCenter,
				iconRotation: getRotationHandleIconRotation(point, iconCenter),
				iconSizePx: ROTATION_HANDLE_SIZE_PX,
			};
		});
	}

	private createBoxHandles(
		rect: ISelectionRect,
		config: {
			rotateTarget: SpaceItem;
			item?: Markup3D;
			allowScaling: boolean;
			allowRotation: boolean;
		},
	) {
		if (config.allowRotation) {
			for (const handle of this.buildBoxRotationHandleDescriptors(
				rect,
				config.rotateTarget,
				this.getOwnerType(config.rotateTarget),
				ROTATION_HANDLE_SIZE_PX,
				null,
				!config.allowScaling,
			)) {
				this.renderRotationHandle(handle);
			}
		}

		if (!config.allowScaling || !config.item) {
			return;
		}

		const bbox = this.getBoundingBoxFromRect(rect);

		for (const corner of selectionHandleCorners) {
			const center = getBoxCornerPoint(bbox, corner, rect.orientation);
			const hoverHandleId = `${this.getOwnerType(config.rotateTarget)}-rotate-${corner}-${config.rotateTarget.id}`;

			this.createScaleHandle(
				center,
				{
					type: "scale",
					id: `${config.item.id}-scale-${corner}`,
					item: config.item,
					corner,
				},
				hoverHandleId,
				rect.orientation,
			);
		}
	}

	private createLinearMarkupHandles(markup: Markup3D, allowRotation: boolean) {
		const points = this.getDisplayedMarkupPoints(markup);

		if (points.length < 2) {
			return;
		}

		const iconOffset = getRotationHandleOffsetDistance(
			ROTATION_HANDLE_SIZE_PX,
			this._spaceViewRenderer.correctionMultiplier.current,
			this.cameraZoomLevel,
		);
		const handleRotation = getLinearSelectionHandleRotation(points[0], points[1]);

		points.forEach((point: PointDouble, endpointIndex: number) => {
			const hoverHandleId = `${markup.id}-line-rotate-${endpointIndex}`;
			const iconCenter = getLinearRotationHandleIconCenter(points[0], points[1], endpointIndex as 0 | 1, iconOffset);

			this.createScaleHandle(
				point,
				{
					type: "scaleLineEndpoint",
					id: `${markup.id}-line-scale-${endpointIndex}`,
					item: markup,
					endpointIndex: endpointIndex as 0 | 1,
				},
				hoverHandleId,
				handleRotation,
			);

			if (allowRotation) {
				this.renderRotationHandle({
					descriptor: {
						id: hoverHandleId,
						kind: "rotate",
						ownerType: "markup",
						priority: getSelectionHandlePriority("markup"),
						zone: createZone(point, this.getWorldHitSize()),
						hoverVisible: true,
					},
					action: {
						type: "rotate",
						id: hoverHandleId,
						rotateTarget: markup,
					},
					zoneRotation: handleRotation,
					triggerCenter: point,
					iconCenter,
					iconSizePx: ROTATION_HANDLE_SIZE_PX,
					iconRotation: getRotationHandleIconRotation(point, iconCenter),
				});
			}
		});
	}

	private createLinearMarkupTextBox(
		markup: Markup3D,
		isInteractive: boolean,
		allowRotation: boolean = isInteractive,
		showConnector: boolean = isInteractive,
	) {
		const rect = this.getTextSelectionRectangle(markup);
		const supportsIndependentTextBoxControls = doesV5LinearMarkupSupportIndependentTextBoxControls(markup.type);

		if (!rect) {
			return;
		}

		if (!supportsIndependentTextBoxControls) {
			return;
		}

		this.createSelectionBox(rect);

		if (showConnector) {
			this.createLinearMarkupTextConnector(markup, rect);
		}

		if (!isInteractive) {
			return;
		}

		const bbox = this.getBoundingBoxFromRect(rect);
		const hitSize = this.getWorldHitSize();

		for (const corner of selectionHandleCorners) {
			const center = getBoxCornerPoint(bbox, corner, rect.orientation);
			const rotationIconInsetPoint = getBoxRotationHandleInsetPoint(
				center,
				corner,
				this._spaceViewRenderer.correctionMultiplier.current,
				this.cameraZoomLevel,
				rect.orientation,
			);
			const hoverHandleId = `${markup.id}-text-rotate-${corner}`;

			this.createScaleHandle(
				center,
				{
					type: "scaleText",
					id: `${markup.id}-text-scale-${corner}`,
					item: markup,
					corner,
				},
				hoverHandleId,
				rect.orientation,
			);

			if (allowRotation) {
				this.renderRotationHandle({
					descriptor: {
						id: hoverHandleId,
						kind: "rotate",
						ownerType: "markupText",
						priority: getSelectionHandlePriority("markupText"),
						zone: createZone(getBoxRotationHandleZoneCenter(center, corner, hitSize, rect.orientation), hitSize),
						corner,
						hoverVisible: true,
					},
					action: {
						type: "rotateText",
						id: hoverHandleId,
						item: markup,
						corner,
					},
					zoneRotation: rect.orientation,
					triggerCenter: center,
					cornerRadius: {x: 0, y: 0},
					iconCenter: this.getCornerRotationIconCenter(rotationIconInsetPoint, corner, rect.orientation, ROTATION_HANDLE_SIZE_PX),
					iconSizePx: ROTATION_HANDLE_SIZE_PX,
					iconRotation: getBoxRotationHandleIconRotation(corner, rect.orientation),
				});
			}
		}

		this.createLinearMarkupTextAnchorHandle(markup, rect);
	}

	private createLinearMarkupTextConnector(markup: Markup3D, rect: ISelectionRect) {
		const anchor = getSelectionBoxTopCenter(rect.center, rect.size, rect.orientation);

		this._textConnectorSegments.push({
			start: {
				x: markup.position.x,
				y: markup.position.y,
			},
			end: anchor,
		});
	}

	private createLinearMarkupTextAnchorHandle(markup: Markup3D, rect: ISelectionRect) {
		const center = getSelectionBoxTopCenter(rect.center, rect.size, rect.orientation);

		this.createTexturedHandle(
			center,
			SCALE_HANDLE_SIZE_PX,
			this.miscTextures.v5HandleTextures.textAnchor,
			{
				id: `${markup.id}-text-anchor`,
				phase: "handle",
				action: {
					type: "moveTextAnchor",
					id: `${markup.id}-text-anchor`,
					item: markup,
				},
			},
			rect.orientation,
		);
	}

	private createCalloutAnchorHandle(markup: Markup3D, isInteractive: boolean) {
		const mesh = this.createTexturedHandle(markup.target, SCALE_HANDLE_SIZE_PX, this.miscTextures.v5HandleTextures.textAnchor, {
			id: `${markup.id}-anchor`,
			phase: "handle",
			action: {
				type: "anchor",
				id: `${markup.id}-anchor`,
				item: markup,
			},
		});

		if (!isInteractive) {
			disableRaycast(mesh);
		}
	}

	private renderRotationHandle({
		descriptor,
		action,
		triggerCenter,
		iconCenter,
		iconSizePx,
		iconRotation,
		zoneRotation,
		zoneStartsAction,
		cornerRadius,
	}: IRotationHandleDefinition) {
		this.createRotationHandle(descriptor, action, triggerCenter, iconCenter, iconSizePx, iconRotation, zoneStartsAction, zoneRotation, cornerRadius);
	}

	private createRotationHandle(
		descriptor: ISelectionHandleDescriptor,
		action: ISelectionHandleAction,
		triggerCenter: PointDouble = descriptor.zone.center,
		iconCenter: PointDouble = descriptor.zone.center,
		iconSizePx: number = ROTATION_HANDLE_SIZE_PX,
		iconRotation: number = 0,
		zoneStartsAction: boolean = false,
		zoneRotation: number = 0,
		cornerRadius?: PointDouble,
	) {
		this._currentRotationHandleIds.add(descriptor.id);
		this.updateRotationHandleMeshes({
			descriptor,
			action,
			triggerCenter,
			iconCenter,
			iconSizePx,
			iconRotation,
			zoneStartsAction,
			zoneRotation,
			cornerRadius,
		});
	}

	private updateRotationHandleMeshes({
		descriptor,
		action,
		triggerCenter,
		iconCenter,
		iconSizePx,
		iconRotation,
		zoneRotation,
		zoneStartsAction,
		cornerRadius,
	}: IRotationHandleDefinition) {
		const sectorRadius = getRotationHandleSectorOuterRadius(iconSizePx, this._spaceViewRenderer.correctionMultiplier.current, this.cameraZoomLevel);
		const sectorStartAngle = getRotationHandleSectorStartAngle(triggerCenter, iconCenter);
		const iconWorldSize = getWorldSizeFromPixels(iconSizePx, this._spaceViewRenderer.correctionMultiplier.current, this.cameraZoomLevel);
		// Box corners get their own geometries in the corner's local frame (which is rotated by `sectorStartAngle`), the rest use the shared ones
		const zoneOutline = cornerRadius ? getBoxRotationHandleZoneOutline(descriptor.zone.width, cornerRadius) : null;
		const triggerOutline = cornerRadius ? getBoxRotationHandleTriggerOutline(sectorRadius, cornerRadius) : null;
		let meshes = this._rotationHandleMeshes.get(descriptor.id);

		if (!meshes) {
			const zone = new Mesh(
				zoneOutline ? createFanGeometry(zoneOutline.length) : this._spaceViewRenderer.planeGeometry,
				this._rotationHandleZoneMaterial,
			);
			const trigger = new Mesh(
				triggerOutline ? createFanGeometry(triggerOutline.length) : this._rotationHandleSectorGeometry,
				this._rotationHandleTriggerMaterial,
			);
			const icon = new Mesh(
				this._spaceViewRenderer.planeGeometry,
				descriptor.kind === "groupRotate" ? this.groupRotationIconMaterial : this.rotationIconMaterial,
			);

			zone.matrixAutoUpdate = false;
			trigger.matrixAutoUpdate = false;
			icon.matrixAutoUpdate = false;
			zone.name = interactiveHandleName;
			trigger.name = interactiveHandleName;
			icon.name = interactiveHandleName;
			icon.visible = false;
			setRaycastEnabled(trigger, false);
			disableRaycast(icon);

			this._container.add(zone);
			this._container.add(trigger);
			this._container.add(icon);

			meshes = {
				zone,
				icon,
				triggers: [trigger],
			};
			this._rotationHandleMeshes.set(descriptor.id, meshes);
		}

		const zone = meshes.zone;
		const trigger = meshes.triggers[0];
		const icon = meshes.icon;

		if (zoneOutline) {
			updateFanGeometry(zone.geometry, zoneOutline);
			zone.position.set(triggerCenter.x, triggerCenter.y, this.getHandleZPosition());
			zone.scale.set(1, 1, 1);
			zone.rotation.z = sectorStartAngle;
		} else {
			zone.position.set(descriptor.zone.center.x, descriptor.zone.center.y, this.getHandleZPosition());
			zone.scale.set(descriptor.zone.width, descriptor.zone.height, 1);
			zone.rotation.z = zoneRotation;
		}
		zone.userData.selectionHandle = {
			id: descriptor.id,
			phase: "zone",
			action,
			anchorCenter: descriptor.zone.center,
			zoneStartsAction,
			kind: descriptor.kind,
			ownerType: descriptor.ownerType,
		} satisfies ISelectionHandleUserData;
		zone.updateMatrix();

		if (triggerOutline) {
			updateFanGeometry(trigger.geometry, triggerOutline);
			trigger.scale.set(1, 1, 1);
		} else {
			trigger.scale.set(sectorRadius, sectorRadius, 1);
		}

		trigger.position.set(triggerCenter.x, triggerCenter.y, this.getHandleZPosition());
		trigger.rotation.z = sectorStartAngle;
		trigger.userData.selectionHandle = {
			id: descriptor.id,
			phase: "trigger",
			action,
			anchorCenter: descriptor.zone.center,
			kind: descriptor.kind,
			ownerType: descriptor.ownerType,
		} satisfies ISelectionHandleUserData;
		trigger.updateMatrix();

		if (icon.material !== (descriptor.kind === "groupRotate" ? this.groupRotationIconMaterial : this.rotationIconMaterial)) {
			icon.material = descriptor.kind === "groupRotate" ? this.groupRotationIconMaterial : this.rotationIconMaterial;
		}

		icon.position.set(iconCenter.x, iconCenter.y, this.getHandleZPosition());
		icon.scale.set(iconWorldSize, iconWorldSize, 1);
		icon.rotation.z = iconRotation;
		icon.userData.selectionHandle = {
			id: descriptor.id,
			phase: "handle",
			action,
			anchorCenter: descriptor.zone.center,
			kind: descriptor.kind,
			ownerType: descriptor.ownerType,
		} satisfies ISelectionHandleUserData;
		icon.updateMatrix();
	}

	private createScaleHandle(center: PointDouble, action: ISelectionHandleAction, hoverHandleId?: string, rotation: number = 0) {
		this.createTexturedHandle(
			center,
			SCALE_HANDLE_SIZE_PX,
			this.miscTextures.v5HandleTextures.scale,
			{
				id: action.id,
				phase: "handle",
				action,
				hoverHandleId,
			},
			rotation,
		);
	}

	private createTexturedHandle(center: PointDouble, sizePx: number, texture: Texture, data: ISelectionHandleUserData, rotation: number = 0) {
		const worldSize = getWorldSizeFromPixels(sizePx, this._spaceViewRenderer.correctionMultiplier.current, this.cameraZoomLevel);
		const material = new MeshBasicMaterial({map: texture, transparent: true, depthTest: false});
		const mesh = new Mesh(this._spaceViewRenderer.planeGeometry, material);

		mesh.matrixAutoUpdate = false;
		mesh.position.set(center.x, center.y, this.getHandleZPosition());
		mesh.scale.set(worldSize, worldSize, 1);
		mesh.rotation.z = rotation;
		mesh.name = interactiveHandleName;
		mesh.userData.selectionHandle = {
			...data,
			anchorCenter: data.anchorCenter ?? center,
		};
		mesh.updateMatrix();

		this._container.add(mesh);
		this._visibleObjects.push(mesh);

		return mesh;
	}

	private createSelectionBox(rect: ISelectionRect) {
		const bbox = this.getBoundingBoxFromRect(rect);
		const corners = selectionHandleCorners.map((corner) => getBoxCornerPoint(bbox, corner, rect.orientation));

		this._selectionBoxSegments.push(
			{start: corners[0], end: corners[1]},
			{start: corners[1], end: corners[2]},
			{start: corners[2], end: corners[3]},
			{start: corners[3], end: corners[0]},
		);
	}

	private createLineOverlay(markup: Markup3D) {
		const points = this.getDisplayedMarkupPoints(markup);

		if (points.length < 2) {
			return;
		}

		this._lineOverlaySegments.push({
			start: points[0],
			end: points[1],
		});
	}

	private getSelectionRectangle(item: SpaceItem): ISelectionRect | null {
		if (item.spaceItemType === "markup") {
			const markup = item as Markup3D;

			if (isV5LinearMarkupType(markup.type)) {
				return null;
			}

			if (markup.type !== MarkupType.Photo360) {
				return this.getSelectionRectangleFromMarkupGeometry(markup);
			}
		}

		return {
			center: {
				x: item.position.x,
				y: item.position.y,
			},
			size: {
				x: item.scale.x,
				y: item.scale.y,
			},
			orientation: item.orientation,
		};
	}

	private renderGroupItemSelection(item: SpaceItem) {
		if (item.spaceItemType === "markup" && isV5LinearMarkupType((item as Markup3D).type)) {
			this.createLineOverlay(item as Markup3D);
			this.createLinearMarkupTextBox(item as Markup3D, false);
			return;
		}

		const selectionRect = this.getSelectionRectangle(item);

		if (selectionRect) {
			this.createSelectionBox(selectionRect);
		}
	}

	private getSelectionBoundingBox(item: SpaceItem): IBox2 | null {
		if (item.spaceItemType === "markup" && isV5LinearMarkupType((item as Markup3D).type)) {
			const markup = item as Markup3D;
			const points = this.getDisplayedMarkupPoints(markup);
			const textRect = this.getTextSelectionRectangle(markup);
			const bboxes: IBox2[] = [];

			if (points.length > 0) {
				bboxes.push(THREEUtils.calculateBox(points));
			}

			if (textRect) {
				bboxes.push(getSelectionBoxBoundingBox(textRect.center, textRect.size, textRect.orientation));
			}

			return mergeBoundingBoxes(bboxes);
		}

		const selectionRect = this.getSelectionRectangle(item);

		return selectionRect ? getSelectionBoxBoundingBox(selectionRect.center, selectionRect.size, selectionRect.orientation) : null;
	}

	private getTextSelectionRectangle(markup: Markup3D): ISelectionRect | null {
		const textSelectionRect = getTextSelectionRect(markup);

		if (!markup.textContent || !textSelectionRect || !MathUtils.isValidNumber(textSelectionRect.size.x)) {
			return null;
		}

		return textSelectionRect;
	}

	private getSelectionRectangleFromMarkupGeometry(markup: Markup3D): ISelectionRect {
		const geometryData = markup.getDisplayedWorldGeometryData();
		const unrotatedGeometryData = THREEUtils.getRotatedVertices(geometryData, -markup.orientation, {x: 0, y: 0});
		const bbox = THREEUtils.calculateBox(unrotatedGeometryData);
		const padding = getMarkupSelectionPadding(
			markup.type,
			this._spaceViewRenderer.correctionMultiplier.current,
			this.cameraZoomLevel,
			markup.lineThickness,
		);

		if (padding > 0) {
			bbox.min.x -= padding;
			bbox.min.y -= padding;
			bbox.max.x += padding;
			bbox.max.y += padding;
		}

		const unrotatedCenter = THREEUtils.getCenterOfBoundingBox(bbox);

		return {
			center: THREEUtils.getRotatedVertex(unrotatedCenter, markup.orientation, {x: 0, y: 0}),
			size: THREEUtils.getSizeOfBoundingBox2(bbox),
			orientation: markup.orientation,
		};
	}

	private getSelectionRectangleFromBoundingBox(boundingBox: IBox2, orientation: number) {
		return {
			center: THREEUtils.getCenterOfBoundingBox(boundingBox),
			size: THREEUtils.getSizeOfBoundingBox2(boundingBox),
			orientation,
		};
	}

	private getBoundingBoxFromRect(rect: ISelectionRect): IBox2 {
		return {
			min: {
				x: rect.center.x - rect.size.x / 2,
				y: rect.center.y - rect.size.y / 2,
			},
			max: {
				x: rect.center.x + rect.size.x / 2,
				y: rect.center.y + rect.size.y / 2,
			},
		};
	}

	private getDisplayedMarkupPoints(markup: Markup3D): PointDouble[] {
		return markup.getDisplayedWorldGeometryData().slice(0, 2) as PointDouble[];
	}

	private getCornerRotationIconCenter(
		cornerPoint: PointDouble,
		corner: SelectionHandleCorner,
		orientation: number,
		iconSizePx: number,
		iconOffsetDistance: number = null,
	) {
		const offsetMagnitude =
			iconOffsetDistance ?? getRotationHandleOffsetDistance(iconSizePx, this._spaceViewRenderer.correctionMultiplier.current, this.cameraZoomLevel);

		return getBoxRotationHandleIconCenter(cornerPoint, corner, offsetMagnitude, orientation);
	}

	private getCornerRadiusRatio(rotateTarget: SpaceItem | ItemSelectionBox) {
		const item = rotateTarget as SpaceItem;
		const isStandardXyicon = item.spaceItemType === "xyicon" && (item.modelData as Xyicon)?.iconCategory !== CatalogIconType.ModelParameter;

		return isStandardXyicon ? STANDARD_XYICON_CORNER_RADIUS_RATIO : 0;
	}

	/** The group box's corners are sharp, but when one is also the corner of a selected item, the handle there should hug that item's corner */
	private getGroupCornerRadius(groupEntry: IRotationHandleDefinition, entries: IRotationHandleDefinition[]) {
		const sameAnchorThreshold = this.getWorldSameAnchorThreshold();
		let cornerRadius: PointDouble | null = null;

		for (const entry of entries) {
			const isSameCorner =
				entry.cornerRadius &&
				entry.descriptor.ownerType !== "group" &&
				doSelectionHandleAnchorsMatch(entry.triggerCenter, groupEntry.triggerCenter, sameAnchorThreshold) &&
				doSelectionHandleAnchorsMatch(entry.descriptor.zone.center, groupEntry.descriptor.zone.center, sameAnchorThreshold);

			if (isSameCorner) {
				// With multiple items sharing the corner, the least rounded one covers the others
				cornerRadius = {
					x: Math.min(cornerRadius?.x ?? Infinity, entry.cornerRadius.x),
					y: Math.min(cornerRadius?.y ?? Infinity, entry.cornerRadius.y),
				};
			}
		}

		return cornerRadius ?? groupEntry.cornerRadius;
	}

	private getSelectedCanvasItems() {
		return [
			...this._spaceItemController.xyiconManager.selectedItems,
			...this._spaceItemController.boundaryManager.selectedItems,
			...this._spaceItemController.markupManager.selectedItems,
		];
	}

	private getOwnerType(item: SpaceItem): SelectionHandleOwnerType {
		switch (item.spaceItemType) {
			case "xyicon":
				return "xyicon";
			case "boundary":
				return "boundary";
			default:
				return "markup";
		}
	}

	private clearRenderedObjects() {
		this.clearTransientObjects();
		this.clearRotationHandleMeshes();
		this.resetLineSegments();
	}

	private clearTransientObjects() {
		for (const object of this._visibleObjects.splice(0)) {
			// Since three.js r186, every Object3D has a dispose() method, so it can't be used to detect the custom disposables
			if (!("isObject3D" in object)) {
				object.dispose();
				continue;
			}

			object.parent?.remove(object as Object3D);
			if ((object as Mesh).geometry !== this._spaceViewRenderer.planeGeometry) {
				((object as Mesh).geometry as BufferGeometry)?.dispose?.();
			}
			((object as Mesh).material as MeshBasicMaterial | LineBasicMaterial | LineDashedMaterial)?.dispose?.();
		}
	}

	private clearRotationHandleMeshes() {
		for (const [id, meshes] of this._rotationHandleMeshes) {
			this.removeRotationHandleMeshes(meshes);
			this._rotationHandleMeshes.delete(id);
		}
	}

	private removeRotationHandleMeshes(meshes: IRotationHandleMeshes) {
		meshes.zone.parent?.remove(meshes.zone);
		meshes.icon.parent?.remove(meshes.icon);
		this.disposeOwnRotationHandleGeometry(meshes.zone);

		for (const trigger of meshes.triggers) {
			trigger.parent?.remove(trigger);
			this.disposeOwnRotationHandleGeometry(trigger);
		}
	}

	private disposeOwnRotationHandleGeometry(mesh: Mesh) {
		if (mesh.geometry !== this._spaceViewRenderer.planeGeometry && mesh.geometry !== this._rotationHandleSectorGeometry) {
			mesh.geometry.dispose();
		}
	}

	private resetLineSegments() {
		this._selectionBoxSegments.length = 0;
		this._lineOverlaySegments.length = 0;
		this._textConnectorSegments.length = 0;
		this._selectionBoxBatch.update([], this.getLineZPosition());
		this._lineOverlayBatch.update([], this.getLineZPosition());
		this._textConnectorBatch.update([], this.getLineZPosition());
	}

	private beginRenderPass() {
		this.clearTransientObjects();
		this._selectionBoxSegments.length = 0;
		this._lineOverlaySegments.length = 0;
		this._textConnectorSegments.length = 0;
		this._currentRotationHandleIds.clear();
		this.syncSharedMaterials();
	}

	private finishRenderPass() {
		this._dashedSelectionLineMaterial.dashSize = 8 * this._spaceViewRenderer.correctionMultiplier.current;
		this._dashedSelectionLineMaterial.gapSize = 8 * this._spaceViewRenderer.correctionMultiplier.current;
		this._selectionBoxBatch.update(this._selectionBoxSegments, this.getLineZPosition());
		this._lineOverlayBatch.update(this._lineOverlaySegments, this.getLineZPosition());
		this._textConnectorBatch.update(this._textConnectorSegments, this.getLineZPosition());
		this.pruneRotationHandleMeshes();
	}

	private resetRenderedState() {
		this.clearTransientObjects();
		this.clearRotationHandleMeshes();
		this.resetLineSegments();
		this._currentRotationHandleIds.clear();
	}

	private syncSharedMaterials() {
		const isDebugVisible = this.isRotationHandleDebugVisible;

		this._rotationHandleZoneMaterial.transparent = isDebugVisible;
		this._rotationHandleZoneMaterial.opacity = isDebugVisible ? 0.25 : 1;
		this._rotationHandleZoneMaterial.colorWrite = isDebugVisible;
		this._rotationHandleZoneMaterial.needsUpdate = true;

		this._rotationHandleTriggerMaterial.transparent = isDebugVisible;
		this._rotationHandleTriggerMaterial.opacity = isDebugVisible ? 0.2 : 1;
		this._rotationHandleTriggerMaterial.colorWrite = isDebugVisible;
		this._rotationHandleTriggerMaterial.needsUpdate = true;
	}

	private pruneRotationHandleMeshes() {
		for (const [id, meshes] of this._rotationHandleMeshes) {
			if (!this._currentRotationHandleIds.has(id)) {
				this.removeRotationHandleMeshes(meshes);
				this._rotationHandleMeshes.delete(id);
			}
		}
	}

	private getLineZPosition() {
		return 1 * this._spaceViewRenderer.correctionMultiplier.current;
	}

	private getHandleZPosition() {
		return 2 * this._spaceViewRenderer.correctionMultiplier.current;
	}

	private get cameraZoomLevel() {
		return this._spaceViewRenderer.toolManager.cameraControls.cameraZoomValue;
	}

	private getWorldHitSize() {
		return getWorldSizeFromPixels(HANDLE_HIT_SIZE_PX, this._spaceViewRenderer.correctionMultiplier.current, this.cameraZoomLevel);
	}

	private getWorldSameAnchorThreshold() {
		return getSelectionHandleSameAnchorThreshold(this._spaceViewRenderer.correctionMultiplier.current, this.cameraZoomLevel);
	}

	private canShowRotationHandleForItem(item: SpaceItem) {
		if (item.spaceItemType === "markup") {
			return true;
		}

		const permission = this._spaceViewRenderer.actions.getModuleTypePermission(item.modelData?.typeId, item.modelData?.ownFeature);

		return doesV5SelectionHandleAllowRotationForPermission(permission);
	}

	private getPinnedRotationHandleIds() {
		return this._activeRotationHandleId ? new Set([this._activeRotationHandleId]) : new Set<string>();
	}

	private get miscTextures() {
		return this._spaceViewRenderer.textureManager.miscTextures;
	}

	private get rotationIconMaterial() {
		if (!this._rotationIconMaterial) {
			this._rotationIconMaterial = new MeshBasicMaterial({
				map: this.miscTextures.v5HandleTextures.rotation,
				transparent: true,
				depthTest: false,
			});
		}

		return this._rotationIconMaterial;
	}

	private get groupRotationIconMaterial() {
		if (!this._groupRotationIconMaterial) {
			this._groupRotationIconMaterial = new MeshBasicMaterial({
				map: this.miscTextures.v5HandleTextures.groupRotation,
				transparent: true,
				depthTest: false,
			});
		}

		return this._groupRotationIconMaterial;
	}

	private get isRotationHandleDebugVisible() {
		return this._spaceViewRenderer.isV5 && DebugInformation.isLoggingEnabled;
	}

	private get _spaceItemController() {
		return this._spaceViewRenderer.spaceItemController;
	}
}
